/**
 * Profile — the "My Profile" page every authenticated user sees.
 *
 * Two cards:
 *   1) Personal Info: avatar (base64 upload ≤ 512 KB), name, phone, email,
 *      and a self-service password change (current password required).
 *   2) Company Info: context-aware. Customers edit their own Customer entity,
 *      admins edit the CMS brand/billing/contact (dealer company), non-admin
 *      staff see it read-only, drivers edit their own Driver record.
 *
 * A single component works for admin/staff (/app/profile), customer
 * (/portal/profile), and driver (/driver/profile) — the backend
 * `/api/profile/me` endpoint resolves the correct context by role.
 */
import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserRound, Building2, KeyRound, Camera, Save, Loader2, ShieldCheck } from "lucide-react";

// ── Helpers ──────────────────────────────────────────────────
const readAsDataUri = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

const Field = ({ label, children, hint }) => (
  <div>
    <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint ? <div className="mt-1 text-[10px] text-muted-foreground">{hint}</div> : null}
  </div>
);

// ── Personal Info ────────────────────────────────────────────
function PersonalInfoCard({ me, refetch }) {
  const [form, setForm] = useState({ name: me.name || "", phone: me.phone || "", email: me.email || "", avatar: me.avatar || "" });
  const fileRef = useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    setForm({ name: me.name || "", phone: me.phone || "", email: me.email || "", avatar: me.avatar || "" });
  }, [me]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 512 * 1024) {
      toast.error("Image must be under 512 KB");
      return;
    }
    try {
      const dataUri = await readAsDataUri(file);
      setForm((f) => ({ ...f, avatar: dataUri }));
    } catch {
      toast.error("Could not read image");
    }
  };

  const save = useMutation({
    mutationFn: (payload) => api.patch("/profile/me", payload).then((r) => r.data),
    onSuccess: () => {
      toast.success("Profile updated");
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to update profile"),
  });

  const isDirty =
    form.name !== (me.name || "") ||
    form.phone !== (me.phone || "") ||
    form.email !== (me.email || "") ||
    form.avatar !== (me.avatar || "");

  return (
    <Card data-testid="profile-personal-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><UserRound size={18} /> Personal information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          <div className="relative">
            <div className="grid h-24 w-24 place-items-center overflow-hidden rounded-full border-2 border-primary/30 bg-muted text-3xl font-black uppercase text-muted-foreground">
              {form.avatar ? (
                <img src={form.avatar} alt="User" className="h-full w-full object-cover" data-testid="profile-avatar-img" />
              ) : (
                (me.name || "?").slice(0, 1)
              )}
            </div>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="absolute -bottom-1 -right-1 rounded-full border border-border bg-background p-1.5 shadow transition-colors hover:bg-primary hover:text-primary-foreground"
              title="Upload new photo"
              data-testid="profile-avatar-upload"
            >
              <Camera size={14} />
            </button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickFile} data-testid="profile-avatar-input" />
          </div>
          <div className="text-xs text-muted-foreground">
            <div className="font-semibold uppercase tracking-widest text-foreground">{me.role}</div>
            <div>Signed in as {me.email}</div>
            <div className="mt-1">Max photo size: 512 KB · square works best</div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Full name">
            <Input value={form.name} onChange={set("name")} data-testid="profile-name" />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={set("phone")} placeholder="+91…" data-testid="profile-phone" />
          </Field>
          <Field label="Email" hint="Changing email will affect your next sign-in">
            <Input type="email" value={form.email} onChange={set("email")} data-testid="profile-email" />
          </Field>
        </div>

        <div>
          <Button
            onClick={() => save.mutate(form)}
            disabled={!isDirty || save.isPending}
            data-testid="profile-save"
          >
            {save.isPending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : <><Save size={14} className="mr-1" /> Save personal info</>}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Password Change ─────────────────────────────────────────
function PasswordCard() {
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const change = useMutation({
    mutationFn: () => api.post("/profile/change-password", { current_password: pwd.current, new_password: pwd.next }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Password changed. You'll need to sign in again next time.");
      setPwd({ current: "", next: "", confirm: "" });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Password change failed"),
  });

  const valid = pwd.current.length > 0 && pwd.next.length >= 8 && pwd.next === pwd.confirm;

  return (
    <Card data-testid="profile-password-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><KeyRound size={18} /> Change password</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <Field label="Current password">
            <Input type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} data-testid="profile-current-password" />
          </Field>
          <Field label="New password" hint="Minimum 8 characters">
            <Input type="password" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} data-testid="profile-new-password" />
          </Field>
          <Field label="Confirm new password">
            <Input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="profile-confirm-password" />
          </Field>
        </div>
        {pwd.confirm && pwd.confirm !== pwd.next ? (
          <div className="text-xs font-semibold text-destructive">Passwords do not match.</div>
        ) : null}
        <Button onClick={() => change.mutate()} disabled={!valid || change.isPending} data-testid="profile-password-save">
          {change.isPending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Changing…</> : <><ShieldCheck size={14} className="mr-1" /> Change password</>}
        </Button>
      </CardContent>
    </Card>
  );
}


function CompanyInputRow({ label, path, form, setForm, editable, disabled = false, type = "text", placeholder = "", hint = "" }) {
  const value = path.split(".").reduce((o, k) => (o ? o[k] : undefined), form) || "";
  const onChange = (e) => {
    const v = e.target.value;
    const parts = path.split(".");
    if (parts.length === 1) setForm({ ...form, [parts[0]]: v });
    else setForm({ ...form, [parts[0]]: { ...(form[parts[0]] || {}), [parts[1]]: v } });
  };
  return (
    <Field label={label} hint={hint}>
      <Input
        type={type}
        value={value}
        onChange={onChange}
        disabled={disabled || !editable}
        placeholder={placeholder}
        data-testid={`profile-company-${path.replace(/\./g, "-")}`}
      />
    </Field>
  );
}

// ── Company Info ────────────────────────────────────────────
function CompanyInfoCard({ me, context, editable, refetch }) {
  const role = me.role;

  const initial = () => {
    if (role === "customer") {
      const c = context || {};
      return {
        name: c.name || "",
        gstin: c.gstin || "",
        pan: c.pan || "",
        billing_address: c.billing_address || "",
        contact_person: c.contact_person || "",
        contact_email: c.contact_email || "",
        contact_phone: c.contact_phone || "",
      };
    }
    if (role === "driver") {
      const d = context || {};
      return {
        name: d.name || "",
        phone: d.phone || "",
        license_no: d.license_no || "",
        license_expiry: d.license_expiry || "",
        aadhaar: d.aadhaar || "",
        blood_group: d.blood_group || "",
        emergency_contact: d.emergency_contact || "",
        address: d.address || "",
      };
    }
    // staff (admin editable, others read-only)
    const brand = context?.brand || {};
    const billing = context?.billing || {};
    const contact = context?.contact || {};
    return {
      brand: {
        name: brand.name || "",
        tagline: brand.tagline || "",
        support_email: brand.support_email || "",
        support_phone: brand.support_phone || "",
      },
      billing: {
        legal_name: billing.legal_name || "",
        gstin: billing.gstin || "",
        pan: billing.pan || "",
        address: billing.address || "",
        bank_name: billing.bank_name || "",
        bank_account: billing.bank_account || "",
        bank_ifsc: billing.bank_ifsc || "",
      },
      contact: {
        email: contact.email || "",
        phone: contact.phone || "",
        address: contact.address || "",
      },
    };
  };

  const [form, setForm] = useState(initial);
  // `initial` is a factory that closes over `context`/`role` — listing those
  // (rather than `initial` itself) triggers rehydration only when the actual
  // upstream data actually changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setForm(initial()), [context, role]);
  const qc = useQueryClient();

  const save = useMutation({
    mutationFn: (data) => api.patch("/profile/company", { data }).then((r) => r.data),
    onSuccess: () => {
      toast.success("Company details saved");
      qc.invalidateQueries({ queryKey: ["cms-landing"] });
      refetch();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to save company"),
  });

  const title =
    role === "customer" ? "Your business" :
    role === "driver" ? "Your driver record" :
    "Dealer company";

  return (
    <Card data-testid="profile-company-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Building2 size={18} /> {title}</CardTitle>
        {!editable ? (
          <div className="text-xs text-muted-foreground">Read-only — only admin users can edit the dealer company from here.</div>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-6">
        {role === "customer" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Business name" path="name" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="GSTIN" path="gstin" placeholder="22AAAAA0000A1Z5" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="PAN" path="pan" placeholder="AAAAA0000A" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Contact person" path="contact_person" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Contact email" path="contact_email" type="email" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Contact phone" path="contact_phone" />
            <div className="md:col-span-2"><CompanyInputRow form={form} setForm={setForm} editable={editable} label="Billing address" path="billing_address" /></div>
          </div>
        )}
        {role === "driver" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Name" path="name" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Phone" path="phone" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Licence no" path="license_no" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Licence expiry" path="license_expiry" type="date" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Aadhaar" path="aadhaar" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Blood group" path="blood_group" placeholder="e.g. O+" />
            <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Emergency contact" path="emergency_contact" />
            <div className="md:col-span-2"><CompanyInputRow form={form} setForm={setForm} editable={editable} label="Address" path="address" /></div>
          </div>
        )}
        {role !== "customer" && role !== "driver" && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Brand</div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Company name" path="brand.name" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Tagline" path="brand.tagline" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Support email" path="brand.support_email" type="email" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Support phone" path="brand.support_phone" />
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Billing / GST</div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Legal name" path="billing.legal_name" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="GSTIN" path="billing.gstin" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="PAN" path="billing.pan" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Bank name" path="billing.bank_name" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Bank A/c" path="billing.bank_account" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="IFSC" path="billing.bank_ifsc" />
              <div className="md:col-span-2"><CompanyInputRow form={form} setForm={setForm} editable={editable} label="Registered address" path="billing.address" /></div>
            </div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">Public contact</div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Contact email" path="contact.email" type="email" />
              <CompanyInputRow form={form} setForm={setForm} editable={editable} label="Contact phone" path="contact.phone" />
              <div className="md:col-span-2"><CompanyInputRow form={form} setForm={setForm} editable={editable} label="Address" path="contact.address" /></div>
            </div>
          </>
        )}
        {editable && (
          <div>
            <Button
              onClick={() => save.mutate(role === "customer" || role === "driver" ? form : form)}
              disabled={save.isPending}
              data-testid="profile-company-save"
            >
              {save.isPending ? <><Loader2 size={14} className="mr-1 animate-spin" /> Saving…</> : <><Save size={14} className="mr-1" /> Save company info</>}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Page ─────────────────────────────────────────────────────
export default function Profile() {
  const { user: authUser } = useAuth();
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["profile-me"],
    queryFn: () => api.get("/profile/me").then((r) => r.data),
  });

  if (isLoading || !data) {
    return <div className="p-6 text-sm text-muted-foreground">Loading profile…</div>;
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="font-display text-3xl font-black tracking-tight">My profile</h1>
        <p className="text-sm text-muted-foreground">
          Update your personal information, change your password, and manage your{" "}
          {authUser?.role === "customer" ? "business" : authUser?.role === "driver" ? "driver record" : "company"} details.
        </p>
      </div>
      <PersonalInfoCard me={data.user} refetch={refetch} />
      <PasswordCard />
      <PushNotificationsCard />
      {data.company_context != null && (
        <CompanyInfoCard
          me={data.user}
          context={data.company_context}
          editable={!!data.company_context_editable}
          refetch={refetch}
        />
      )}
    </div>
  );
}
